// ============================================================================
// Schemas declarativos para exibir a resposta bruta de simulacao de cada
// banco (campo descricaoRespostaBanco / NEG_SIMULACAO.ds_resposta_banco).
//
// Cada banco devolve um objeto com shape proprio:
//   - Bradesco (idBanco=45): result.data
//   - Santander (idBanco=9): response.data.calculateSimulation
//   - Itau (idBanco=61): res.data.data[0]
//
// O componente RespostaBancoCard escolhe o schema pelo idBanco, resolve cada
// path no objeto e renderiza so os campos com valor presente, agrupados.
// ============================================================================

// ---------- ids dos bancos ----------
export const BANCO_IDS = {
  BRADESCO: 45,
  SANTANDER: 9,
  ITAU: 61
};

// ---------- resolver de path aninhado ("a.b.c") ----------
export const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

// ---------- formatadores ----------
const fmtCurrency = (v) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const fmtPercent = (v) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;
};

const fmtMeses = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return `${v} ${Number(v) === 1 ? 'mês' : 'meses'}`;
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  // datas ISO sem hora (YYYY-MM-DD) — evita shift de fuso usando UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) {
    return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }
  return d.toLocaleDateString('pt-BR');
};

const fmtBool = (v) => {
  if (v === true) return 'Sim';
  if (v === false) return 'Não';
  return '—';
};

const fmtString = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

const FORMATTERS = {
  currency: fmtCurrency,
  percent: fmtPercent,
  meses: fmtMeses,
  date: fmtDate,
  bool: fmtBool,
  string: fmtString
};

export const formatValue = (value, format) => (FORMATTERS[format] ?? fmtString)(value);

// Um valor e considerado "vazio" (campo omitido na tela) quando null/undefined
// ou string vazia. Zero e false NAO sao vazios (0% / "Não" sao informacao util).
export const isEmptyValue = (value) =>
  value === null || value === undefined || value === '';

// ============================================================================
// Schemas por banco
// ============================================================================

const BRADESCO_SCHEMA = [
  {
    group: 'Identificação',
    fields: [
      { label: 'Carteira', path: 'descricaoCarteira', format: 'string' },
      { label: 'Código da Carteira', path: 'codigoCarteira', format: 'string' },
      { label: 'Sistema de Amortização', path: 'tipoSistemaAmortizador', format: 'string' },
      { label: 'Indexador', path: 'indexador', format: 'string' },
      { label: 'Garantia', path: 'descricaoGarantiaOperacao', format: 'string' },
      { label: 'Data da Simulação', path: 'dataSimulacao', format: 'date' },
      { label: 'Usa FGTS', path: 'indicadorFgts', format: 'bool' }
    ]
  },
  {
    group: 'Valores do Financiamento',
    fields: [
      { label: 'Valor do Imóvel', path: 'valorImovel', format: 'currency' },
      { label: 'Valor Financiado', path: 'valorFinanciamento', format: 'currency' },
      { label: 'Valor Liberado ao Cliente', path: 'valorLiberadoCliente', format: 'currency' },
      { label: '% Liberado ao Cliente', path: 'percentualValorLiberadoCliente', format: 'percent' },
      { label: 'Valor Total do Financiamento', path: 'valorTotalFinanciamento', format: 'currency' },
      { label: 'Prazo', path: 'prazoFinanciamento', format: 'meses' }
    ]
  },
  {
    group: 'Taxas e Custos',
    fields: [
      { label: 'Taxa Juros Efetiva Anual', path: 'valorTaxaJurosEfetivoAno', format: 'percent' },
      { label: 'Taxa Nominal', path: 'valorTaxaNominal', format: 'percent' },
      { label: 'CET Anual', path: 'valorCetAno', format: 'percent' },
      { label: 'CESH Anual', path: 'valorCeshAno', format: 'percent' },
      { label: 'IOF', path: 'valorIof', format: 'currency' },
      { label: 'Tarifa de Avaliação (TAG)', path: 'valorTag', format: 'currency' },
      { label: '% TAG', path: 'percentualTag', format: 'percent' },
      { label: 'Taxa Administração Mensal', path: 'valorTaxaAdministracaoMensal', format: 'currency' },
      { label: 'Total Despesas Financeiras', path: 'valorTotalDespesasFinanceiras', format: 'currency' }
    ]
  },
  {
    group: 'Prestações',
    fields: [
      { label: '1ª Prestação (com seguro + TAC)', path: 'valorPrimeiraPrestacaoComSeguroTac', format: 'currency' },
      { label: '1ª Prestação (sem seguro + TAC)', path: 'valorPrimeiraPrestacaoSemSeguroTac', format: 'currency' },
      { label: 'Última Prestação', path: 'valorUltimaPrestacao', format: 'currency' },
      { label: 'Valor do Seguro', path: 'valorSeguro', format: 'currency' }
    ]
  },
  {
    group: 'Renda e Contratação',
    fields: [
      { label: 'Renda Informada', path: 'valorRendaInformada', format: 'currency' },
      { label: 'Renda Líquida Mínima Exigida', path: 'valorRendaLiquidaMinimaExigida', format: 'currency' },
      { label: 'Valor Devido no Ato', path: 'valorDevidoAtoContratacao', format: 'currency' },
      { label: '% Devido no Ato', path: 'percentualDevidoAtoContratacao', format: 'percent' }
    ]
  }
];

const ITAU_SCHEMA = [
  {
    group: 'Identificação',
    fields: [
      { label: 'ID da Simulação', path: 'simulationId', format: 'string' },
      { label: 'Tipo de Financiamento', path: 'financingType', format: 'string' },
      { label: 'Tipo de Produto', path: 'productType', format: 'string' },
      { label: 'Tipo de Taxa', path: 'feeType', format: 'string' },
      { label: 'Sistema de Amortização', path: 'amortizationType', format: 'string' },
      { label: 'Seguradora', path: 'insuranceType', format: 'string' },
      { label: 'Tipo do Imóvel', path: 'propertyType', format: 'string' }
    ]
  },
  {
    group: 'Valores do Financiamento',
    fields: [
      { label: 'Valor do Imóvel', path: 'propertyPrice', format: 'currency' },
      { label: 'Valor Financiado', path: 'loanAmount', format: 'currency' },
      { label: 'Entrada', path: 'downPayment', format: 'currency' },
      { label: 'Prazo', path: 'period', format: 'meses' },
      { label: 'Total das Parcelas', path: 'installmentsTotalValue', format: 'currency' }
    ]
  },
  {
    group: 'Taxas e Custos',
    fields: [
      { label: 'Taxa de Juros Anual', path: 'annualRate', format: 'percent' },
      { label: 'Taxa de Juros Mensal', path: 'monthlyRate', format: 'percent' },
      { label: 'CET Anual', path: 'cetAnnual', format: 'percent' },
      { label: 'CEM Anual', path: 'cemAnnual', format: 'percent' },
      { label: 'CESH Anual', path: 'ceshAnnual', format: 'percent' },
      { label: 'IOF Total', path: 'iof.totalValue', format: 'currency' }
    ]
  },
  {
    group: 'Primeira Parcela',
    fields: [
      { label: 'Valor Total', path: 'firstInstallment.totalValue', format: 'currency' },
      { label: 'Amortização', path: 'firstInstallment.amortization', format: 'currency' },
      { label: 'Juros', path: 'firstInstallment.interest', format: 'currency' },
      { label: 'Seguro MIP', path: 'firstInstallment.insurerMip', format: 'currency' },
      { label: 'Seguro DFI', path: 'firstInstallment.insurerDfi', format: 'currency' },
      { label: 'Vencimento', path: 'firstInstallment.dueDate', format: 'date' }
    ]
  },
  {
    group: 'Última Parcela',
    fields: [
      { label: 'Valor Total', path: 'lastInstallment.totalValue', format: 'currency' },
      { label: 'Amortização', path: 'lastInstallment.amortization', format: 'currency' },
      { label: 'Juros', path: 'lastInstallment.interest', format: 'currency' },
      { label: 'Vencimento', path: 'lastInstallment.dueDate', format: 'date' }
    ]
  },
  {
    group: 'Opções',
    fields: [
      { label: 'Inclui IOF', path: 'includeIof', format: 'bool' },
      { label: 'Inclui Custos de Registro', path: 'includeRegistryCosts', format: 'bool' },
      { label: 'Inclui Avaliação do Imóvel', path: 'includePropertyEvaluation', format: 'bool' }
    ]
  }
];

// Santander: paths confirmados a partir de resposta real
// (response.data.calculateSimulation). O array paymentPlan (centenas de
// parcelas) e os campos *Key (codigos internos) sao intencionalmente
// omitidos. O grupo "Taxas com Oferta" (relationShipFlow) so aparece quando
// ha oferta de relacionamento — vem null em "SEM RELACIONAMENTO" e o
// componente omite o grupo automaticamente.
const SANTANDER_SCHEMA = [
  {
    group: 'Identificação',
    fields: [
      { label: 'ID da Simulação', path: 'simulationId', format: 'string' },
      { label: 'Carteira', path: 'customerPortfolioName', format: 'string' },
      { label: 'Sistema de Amortização', path: 'amortizationType', format: 'string' },
      { label: 'Indexador', path: 'trIndexer', format: 'string' },
      { label: 'Objetivo do Financiamento', path: 'financingObjective', format: 'string' },
      { label: 'Seguradora', path: 'insurer', format: 'string' },
      { label: 'Segmento', path: 'segment', format: 'string' }
    ]
  },
  {
    group: 'Valores do Financiamento',
    fields: [
      { label: 'Valor do Imóvel', path: 'propertyValue', format: 'currency' },
      { label: 'Valor Financiado', path: 'financingValue', format: 'currency' },
      { label: 'Entrada', path: 'downPaymentAmount', format: 'currency' },
      { label: 'Despesas Financiadas', path: 'expensesFinancedValue', format: 'currency' },
      { label: 'Valor Total c/ Despesas', path: 'totalFinancingValueWithExpenses', format: 'currency' },
      { label: 'Prazo', path: 'financingDeadlineInMonths', format: 'meses' },
      { label: 'Prazo Mínimo', path: 'minimumFinancingDeadlineInMonths', format: 'meses' },
      { label: 'Prazo Máximo', path: 'maximumFinancingDeadlineInMonths', format: 'meses' }
    ]
  },
  {
    group: 'Taxas e Custos (Padrão)',
    fields: [
      { label: 'Taxa de Juros Anual', path: 'unrelatedFlow.annualInterestRate', format: 'percent' },
      { label: 'Taxa de Juros Mensal', path: 'unrelatedFlow.monthlyInterestRate', format: 'percent' },
      { label: 'CET Anual', path: 'unrelatedFlow.cetRate', format: 'percent' },
      { label: 'CESH Anual', path: 'unrelatedFlow.ceshRate', format: 'percent' },
      { label: 'Primeira Parcela', path: 'unrelatedFlow.firstPaymentAmount', format: 'currency' },
      { label: 'Última Parcela', path: 'unrelatedFlow.lastPaymentAmount', format: 'currency' },
      { label: 'IOF', path: 'iofValue', format: 'currency' },
      { label: 'Tarifa de Avaliação', path: 'valuationFeeAmount', format: 'currency' }
    ]
  },
  {
    group: 'Taxas com Oferta de Relacionamento',
    fields: [
      { label: 'Taxa de Juros Anual', path: 'relationShipFlow.annualInterestRate', format: 'percent' },
      { label: 'Taxa de Juros Mensal', path: 'relationShipFlow.monthlyInterestRate', format: 'percent' },
      { label: 'CET Anual', path: 'relationShipFlow.cetRate', format: 'percent' },
      { label: 'CESH Anual', path: 'relationShipFlow.ceshRate', format: 'percent' },
      { label: 'Primeira Parcela', path: 'relationShipFlow.firstPaymentAmount', format: 'currency' }
    ]
  },
  {
    group: 'Relacionamento e Campanha',
    fields: [
      { label: 'Oferta de Relacionamento', path: 'relationShipOffer', format: 'string' },
      { label: 'Campanha', path: 'campaign', format: 'string' }
    ]
  },
  {
    group: 'Permissões de Financiamento',
    fields: [
      { label: 'Permite FGTS', path: 'allowsFGTS', format: 'bool' },
      { label: 'Permite Financiar IOF', path: 'allowsToFinanceIOF', format: 'bool' },
      { label: 'Permite Financiar Tarifa de Avaliação', path: 'allowsToFinanceWarrantyEvaluationFee', format: 'bool' },
      { label: 'Permite Financiar Registro e ITBI', path: 'allowsToFinancePropertyRegistrationAndITBI', format: 'bool' }
    ]
  }
];

const SCHEMAS_BY_BANCO = {
  [BANCO_IDS.BRADESCO]: BRADESCO_SCHEMA,
  [BANCO_IDS.SANTANDER]: SANTANDER_SCHEMA,
  [BANCO_IDS.ITAU]: ITAU_SCHEMA
};

export const getSchemaByBanco = (idBanco) => SCHEMAS_BY_BANCO[Number(idBanco)] ?? null;
